/**
 * Real self-fetching leave-types hook (#wagepro-leave-phase2). Read-only
 * list of active leave types for the entity, used by:
 *  - The "Record leave" drawer (employee leave-type picker).
 *  - The "Leave types" settings panel.
 *
 * Mirrors `use_leave_balances.ts`'s DI-seam + state-machine pattern exactly.
 *
 * ── entitlement_required vs error ──────────────────────────────────────────
 *
 * `payroll.listLeaveTypes` is a `payrollEntityProcedure` (gated by
 * `requirePayrollAddon`), so a missing `payroll` add-on grant resolves
 * `entitlement_required`, not `error` (classified via
 * `isPayrollAddonRequiredError`). Every other FORBIDDEN resolves `error`.
 *
 * ── Upsert write path ──────────────────────────────────────────────────────
 *
 * `upsert` is `undefined` unless BOTH of:
 *   - the read resolved `ready`, AND
 *   - `isWriteCapableGrant(grant)` — a read-only token never sees the edit
 *     affordances.
 */
import * as React from 'react';
import {
  getTrpcClient,
  isPayrollAddonRequiredError,
  isForbiddenTrpcError,
} from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { useAuth } from '../lib/auth/auth_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';

/**
 * `leave_types.accrual_method`. `fixed_annual` grants the yearly allowance
 * up-front, `monthly` credits 1/12 of it each month (both via the backend
 * entitlement engine); `statutory` is pay-run accrued; `none` never accrues.
 */
export type LeaveAccrualMethod =
  | 'statutory'
  | 'fixed_annual'
  | 'monthly'
  | 'none';

/** Wire shape of one leave type returned by `payroll.listLeaveTypes`. */
export interface RealLeaveTypeRecord {
  id: string;
  code: string;
  name: string;
  is_paid: number;
  tracks_balance: number;
  accrual_method: LeaveAccrualMethod;
  annual_entitlement_days_scaled: number | null;
  /**
   * Entitlement-engine policy fields. Optional on the wire only so a
   * response from a backend that predates them still adapts cleanly —
   * `adaptLeaveType` applies the backend's own column defaults.
   */
  prorate_on_join?: number;
  carry_forward_max_days_scaled?: number | null;
  leave_year_start_month?: number;
  is_system: number;
  is_archived: number | boolean;
}

/** UI-facing leave type with boolean flags derived from integer columns. */
export interface LeaveType {
  id: string;
  code: string;
  name: string;
  is_paid: boolean;
  tracks_balance: boolean;
  accrual_method: LeaveAccrualMethod;
  /** Days × 100 per leave year. `null` means "not yet configured". */
  annual_entitlement_days_scaled: number | null;
  /** Pro-rate the entitlement from the employee's start date. */
  prorate_on_join: boolean;
  /** Days × 100 that may carry into the next leave year; `null` = unlimited. */
  carry_forward_max_days_scaled: number | null;
  /** 1–12: the calendar month the leave year starts in. */
  leave_year_start_month: number;
  is_system: boolean;
  is_archived: boolean;
}

export interface LeaveTypesFetchInput {
  firm_id: string;
  entity_id: string;
}

export interface LeaveTypesFetchDeps {
  listLeaveTypes: (
    input: LeaveTypesFetchInput,
  ) => Promise<{ leave_types: RealLeaveTypeRecord[] }>;
}

function defaultLeaveTypesFetchDeps(): LeaveTypesFetchDeps {
  const client = getTrpcClient();
  return {
    // The tRPC inferred output narrows `accrual_method` as `string` and omits
    // `is_archived` from the JSON projection at the router boundary — both are
    // present at runtime. Cast at the wire deserialization seam; the adapter
    // (`adaptLeaveType`) immediately narrows to `LeaveType` with proper types.
    listLeaveTypes: (input) =>
      client.payroll.listLeaveTypes
        .query(input)
        .then((r) => r as unknown as { leave_types: RealLeaveTypeRecord[] }),
  };
}

export type LeaveTypesLoadFailure = 'entitlement_required' | 'error';

function classifyLoadFailure(err: unknown): LeaveTypesLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

export type LeaveTypesFetchResult =
  | { outcome: 'success'; leave_types: LeaveType[] }
  | { outcome: 'failure'; failure: LeaveTypesLoadFailure };

function adaptLeaveType(raw: RealLeaveTypeRecord): LeaveType {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    is_paid: raw.is_paid === 1,
    tracks_balance: raw.tracks_balance === 1,
    accrual_method: raw.accrual_method,
    annual_entitlement_days_scaled: raw.annual_entitlement_days_scaled,
    prorate_on_join: (raw.prorate_on_join ?? 1) === 1,
    carry_forward_max_days_scaled: raw.carry_forward_max_days_scaled ?? null,
    leave_year_start_month: raw.leave_year_start_month ?? 1,
    is_system: raw.is_system === 1,
    is_archived:
      typeof raw.is_archived === 'boolean'
        ? raw.is_archived
        : raw.is_archived === 1,
  };
}

export async function fetchLeaveTypes(
  input: LeaveTypesFetchInput,
  deps?: LeaveTypesFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<LeaveTypesFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveTypesFetchDeps();
    const { leave_types } = await resolvedDeps.listLeaveTypes(input);
    return {
      outcome: 'success',
      leave_types: leave_types.map(adaptLeaveType),
    };
  } catch (err) {
    if (!isCancelled()) {
      console.error('[wagepro-web] Failed to load leave types:', err);
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

export type LeaveTypesState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; leave_types: LeaveType[] };

export function resolveLeaveTypesState(input: {
  entityContext: OAuthEntityContext;
  leave_types: LeaveType[] | undefined;
  failure: LeaveTypesLoadFailure | undefined;
}): LeaveTypesState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.leave_types !== undefined) {
    return { status: 'ready', leave_types: input.leave_types };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

// ── Write path: upsert ─────────────────────────────────────────────────────

export type LeaveTypeUpsertFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'not_found'
  | 'error';

export type LeaveTypeUpsertResult =
  | { outcome: 'success'; id: string; created: boolean }
  | { outcome: 'failure'; failure: LeaveTypeUpsertFailure };

export interface UpsertLeaveTypeInput {
  code: string;
  name: string;
  is_paid: boolean;
  tracks_balance: boolean;
  accrual_method: LeaveAccrualMethod;
  annual_entitlement_days_scaled: number | null;
  prorate_on_join?: boolean;
  carry_forward_max_days_scaled?: number | null;
  leave_year_start_month?: number;
  is_archived?: boolean;
}

export interface LeaveTypesUpsertDeps {
  upsertLeaveType: (
    input: LeaveTypesFetchInput & UpsertLeaveTypeInput,
  ) => Promise<{ id: string; created: boolean }>;
}

function defaultLeaveTypesUpsertDeps(): LeaveTypesUpsertDeps {
  const client = getTrpcClient();
  return {
    upsertLeaveType: (input) => client.payroll.upsertLeaveType.mutate(input),
  };
}

function classifyUpsertFailure(err: unknown): LeaveTypeUpsertFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  if (isForbiddenTrpcError(err)) return 'forbidden';
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const data = (err as { data?: { code?: unknown } }).data;
    if (data?.code === 'NOT_FOUND') return 'not_found';
  }
  return 'error';
}

export async function upsertLeaveType(
  entityInput: LeaveTypesFetchInput,
  typeInput: UpsertLeaveTypeInput,
  deps?: LeaveTypesUpsertDeps,
): Promise<LeaveTypeUpsertResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveTypesUpsertDeps();
    const result = await resolvedDeps.upsertLeaveType({
      ...entityInput,
      ...typeInput,
    });
    return { outcome: 'success', id: result.id, created: result.created };
  } catch (err) {
    console.error('[wagepro-web] leave: upsert leave type failed:', err);
    return { outcome: 'failure', failure: classifyUpsertFailure(err) };
  }
}

export interface UseLeaveTypesResult {
  state: LeaveTypesState;
  leave_types: LeaveType[];
  is_loading: boolean;
  retry: () => void;
  /** `undefined` when the write path isn't reachable (read-only grant or state not ready). */
  upsert:
    | ((typeInput: UpsertLeaveTypeInput) => Promise<LeaveTypeUpsertResult>)
    | undefined;
}

export function useLeaveTypes(): UseLeaveTypesResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [leave_types, setLeaveTypes] = React.useState<LeaveType[] | undefined>(
    undefined,
  );
  const [failure, setFailure] = React.useState<
    LeaveTypesLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setLeaveTypes(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchLeaveTypes(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setLeaveTypes(result.leave_types);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = React.useMemo(
    () => resolveLeaveTypesState({ entityContext, leave_types, failure }),
    [entityContext, leave_types, failure],
  );

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? entityContext
      : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const upsertFn = canWrite
    ? (typeInput: UpsertLeaveTypeInput): Promise<LeaveTypeUpsertResult> =>
        upsertLeaveType(
          {
            firm_id: readyEntity.firm_id,
            entity_id: readyEntity.entity_id,
          },
          typeInput,
        ).then((result) => {
          if (result.outcome === 'success') retry();
          return result;
        })
    : undefined;

  return {
    state,
    leave_types: state.status === 'ready' ? state.leave_types : [],
    is_loading: state.status === 'loading',
    retry,
    upsert: upsertFn,
  };
}
